import { useState, useEffect, useRef } from "react";
import { useAtom, useAtomValue } from "lib/global-context";
import { cn } from "lib/utils";
import { goalTimelineAtom, ageAtom } from "lib/atoms";
import { Input } from "components/ui/input";
import { debounce } from "lodash";
import styles from "./input-timeline.module.css";

interface InputTimelineProps {
  config: {
    min?: number;
    max?: number;
    displayConfiguration: {
      inputType?: string;
      title: string;
      name: string;
      placeholder: string;
    };
  };
}

const InputTimeline = ({ config }: InputTimelineProps) => {
  const { min, max, displayConfiguration } = config;
  const { inputType, title, name, placeholder } = displayConfiguration;

  const [timeline, setTimeline] = useAtom(goalTimelineAtom);
  const age = useAtomValue(ageAtom);
  const [tempValue, setTempValue] = useState<string>(
    timeline !== null ? timeline.toString() : "",
  );
  const [error, setError] = useState<string | null>(null);

  const validateTimeline = debounce((value: string) => {
    const timelineValue = value === "" ? null : Number(value);
    setTimeline(timelineValue);

    if (!timelineValue || timelineValue < (min || Number.MIN_SAFE_INTEGER)) {
      setError(`The goal must take at least ${min} years`);
    } else if (
      age !== null &&
      timelineValue + age > (max || Number.MAX_SAFE_INTEGER)
    ) {
      setError(`You cannot exceed age ${max}.`);
    } else {
      setError(null);
    }
  }, 300);

  const handleTimelineChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;

    if (/^[0-9]*$/.test(value)) {
      setTempValue(value);
      validateTimeline(value);
    }
  };

  const isFirstRender = useRef(true);
  useEffect(() => {
    if (isFirstRender.current) {
      isFirstRender.current = false;
      return;
    }

    if (age !== null && age !== undefined) {
      validateTimeline(tempValue);
    }
  }, [age, tempValue, validateTimeline]);

  return (
    <div className={styles.container}>
      <label
        htmlFor={name}
        className={cn(styles.label, error && styles.errorLabel)}
      >
        {error || title}
      </label>
      <div className={styles.inputWrapper}>
        <Input
          id={name}
          type={inputType}
          placeholder={placeholder}
          value={tempValue}
          onChange={handleTimelineChange}
          className={cn(error && styles.errorInput)}
          required
        />
        <span className={styles.yearsLabel}>{`years`}</span>
      </div>
    </div>
  );
};

export default InputTimeline;
